import { useNavigate } from "react-router-dom";
import { ArrowDown, ArrowUp, Sparkles } from "lucide-react";
import {
  formatAvgMrrDisplay,
  formatGrowthDisplay,
  growthTierForOpportunity,
} from "../../lib/opportunityMarketRanking";

const TIER_LABEL = {
  hot: "Hot",
  rising: "Rising",
  stable: "Stable",
  cooling: "Cooling",
};

const TIER_STYLE = {
  hot: "bg-rose-500/15 text-rose-200 border-rose-500/30",
  rising: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30",
  stable: "bg-zinc-500/15 text-zinc-300 border-zinc-500/25",
  cooling: "bg-sky-500/15 text-sky-200 border-sky-500/28",
};

function MovementCell({ movement }) {
  if (movement === "NEW" || movement?.kind === "NEW") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-violet-500/35 bg-violet-500/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-violet-200">
        <Sparkles className="h-3 w-3" />
        NEW
      </span>
    );
  }
  if (movement?.kind === "UP" && movement.delta != null && movement.delta > 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-emerald-400 tabular-nums">
        <ArrowUp className="h-3.5 w-3.5" />
        ↑ +{movement.delta}
      </span>
    );
  }
  if (movement?.kind === "DOWN" && movement.delta != null && movement.delta > 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-rose-400/90 tabular-nums">
        <ArrowDown className="h-3.5 w-3.5" />
        ↓ −{movement.delta}
      </span>
    );
  }
  if (movement?.dir === "up") {
    return (
      <span className="inline-flex items-center gap-0.5 text-emerald-400 tabular-nums">
        <ArrowUp className="h-3.5 w-3.5" />↑ +{movement.delta}
      </span>
    );
  }
  if (movement?.dir === "down") {
    return (
      <span className="inline-flex items-center gap-0.5 text-rose-400/90 tabular-nums">
        <ArrowDown className="h-3.5 w-3.5" />
        ↓ −{movement.delta}
      </span>
    );
  }
  return <span className="text-zinc-600">—</span>;
}

/**
 * Top 10 from server-side snapshot (stable ranking; movement vs prior snapshot).
 *
 * @param {{ snapshot: object | null, loadingSnapshot?: boolean, snapshotError?: string | null }} props
 */
export default function TopPerformingOpportunitiesTable({ snapshot, loadingSnapshot = false, snapshotError = null }) {
  const navigate = useNavigate();

  const loading = Boolean(loadingSnapshot);
  const error = snapshotError;

  const entries = Array.isArray(snapshot?.entries) ? snapshot.entries : [];
  const updatedLabel = snapshot?.updated_label;
  const message = snapshot?.message;
  const dataNote = snapshot?.data_quality_note;
  const rankedRows = entries;

  if (loading && entries.length === 0) {
    return (
      <div className="mb-8 md:mb-10 overflow-hidden rounded-2xl border border-[#1F1F1F] bg-[#0c0c0c] shadow-[0_20px_60px_rgba(0,0,0,0.35)]">
        <div className="flex flex-col gap-1 border-b border-[#1F1F1F] bg-gradient-to-r from-[#7C3AED]/10 via-transparent to-amber-500/5 px-4 py-4 md:flex-row md:items-center md:justify-between md:px-6">
          <div>
            <h2
              className="text-lg font-bold text-white md:text-xl"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              Top 10 · Market intelligence
            </h2>
            <p className="text-xs text-zinc-500">Loading snapshot…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-8 md:mb-10 overflow-hidden rounded-2xl border border-[#1F1F1F] bg-[#0c0c0c] shadow-[0_20px_60px_rgba(0,0,0,0.35)]">
      <div className="flex flex-col gap-1 border-b border-[#1F1F1F] bg-gradient-to-r from-[#7C3AED]/10 via-transparent to-amber-500/5 px-4 py-4 md:flex-row md:items-center md:justify-between md:px-6">
        <div>
          <h2 className="text-lg font-bold text-white md:text-xl" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Top 10 · Market intelligence
          </h2>
          <p className="text-xs text-zinc-500">
            Ranked by verified revenue, growth, and market signals
            {updatedLabel ? (
              <>
                {" "}
                · <span className="text-zinc-400">{updatedLabel}</span>
              </>
            ) : null}
          </p>
          {dataNote ? <p className="mt-1 text-[11px] text-zinc-600">{dataNote}</p> : null}
        </div>
      </div>

      {/* Mobile-first leaderboard (no horizontal scrolling) */}
      <div className="md:hidden px-3 py-3">
        {rankedRows.length > 0 ? (
          <div className="space-y-2.5">
            {rankedRows.map((row) => {
              const id = row.opportunity_id;
              const opp = {
                display_mrr_usd: row.display_mrr_usd,
                display_growth_30d: row.display_growth_30d,
                market_avg_mrr_usd: row.market_avg_mrr_usd,
                market_avg_growth_30d: row.market_avg_growth_30d,
                signal_count: row.signal_count,
                momentum: row.momentum,
                trend_score: row.trend_score,
                estimated_mrr_min: row.estimated_mrr_min,
                estimated_mrr_max: row.estimated_mrr_max,
              };
              const tier = growthTierForOpportunity(opp);
              return (
                <button
                  key={id || `m-rank-${row.rank}`}
                  type="button"
                  onClick={() => id && navigate(`/dashboard/opportunities/${id}`)}
                  className="w-full rounded-xl border border-[#1F1F1F] bg-white/[0.02] p-3 text-left"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">#{row.rank}</p>
                      <p className="line-clamp-2 text-sm font-semibold text-zinc-100">{row.name || "—"}</p>
                    </div>
                    <span className={`shrink-0 inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold ${TIER_STYLE[tier]}`}>
                      {TIER_LABEL[tier]}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-400">
                    <span className="font-medium text-zinc-200">{formatAvgMrrDisplay(opp)}</span>
                    <span>{formatGrowthDisplay(opp)}</span>
                    <span className="text-zinc-500">{row.niche || "—"}</span>
                    <MovementCell movement={row.movement} />
                  </div>
                </button>
              );
            })}
          </div>
        ) : null}
      </div>

      <div className="hidden overflow-x-auto md:block">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead>
            <tr className="border-b border-[#1F1F1F] text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
              <th className="px-3 py-3 pl-4 md:px-4">Rank</th>
              <th className="px-3 py-3 md:px-4">Opportunity</th>
              <th className="px-3 py-3 md:px-4">Avg MRR</th>
              <th className="px-3 py-3 md:px-4">Growth</th>
              <th className="px-3 py-3 md:px-4">Trend</th>
              <th className="px-3 py-3 pr-4 md:px-4">Move</th>
            </tr>
          </thead>
          <tbody>
            {rankedRows.map((row) => {
              const id = row.opportunity_id;
              const rank = row.rank;
              const opp = {
                name: row.name,
                display_mrr_usd: row.display_mrr_usd,
                display_growth_30d: row.display_growth_30d,
                market_avg_mrr_usd: row.market_avg_mrr_usd,
                market_avg_growth_30d: row.market_avg_growth_30d,
                signal_count: row.signal_count,
                momentum: row.momentum,
                trend_score: row.trend_score,
                estimated_mrr_min: row.estimated_mrr_min,
                estimated_mrr_max: row.estimated_mrr_max,
              };
              const tier = growthTierForOpportunity(opp);
              const rowHighlight =
                rank === 1
                  ? "border-l-2 border-l-amber-400/80 bg-amber-500/[0.06]"
                  : rank === 2
                    ? "border-l-2 border-l-zinc-300/50 bg-white/[0.03]"
                    : rank === 3
                      ? "border-l-2 border-l-[#7C3AED]/70 bg-[#7C3AED]/[0.07]"
                      : "border-l-2 border-l-transparent";

              return (
                <tr
                  key={id || `rank-${rank}`}
                  className={`group transition-colors duration-300 hover:bg-white/[0.04] ${rowHighlight}`}
                >
                  <td className="px-3 py-3 pl-4 align-middle md:px-4">
                    <span
                      className={`inline-flex h-8 w-8 items-center justify-center rounded-lg text-sm font-bold tabular-nums ${
                        rank <= 3 ? "text-white" : "text-zinc-400"
                      }`}
                      style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                    >
                      {rank}
                    </span>
                  </td>
                  <td className="max-w-[220px] px-3 py-3 align-middle md:max-w-xs md:px-4">
                    <button
                      type="button"
                      onClick={() => id && navigate(`/dashboard/opportunities/${id}`)}
                      className="text-left font-medium text-zinc-100 transition-colors hover:text-[#C4B5FD]"
                    >
                      <span className="line-clamp-2">{row.name || "—"}</span>
                      {row.niche ? <span className="mt-0.5 block text-[10px] font-normal uppercase tracking-wide text-zinc-600">{row.niche}</span> : null}
                    </button>
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 align-middle tabular-nums text-zinc-200 md:px-4">
                    {formatAvgMrrDisplay(opp)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 align-middle tabular-nums text-zinc-300 md:px-4">
                    {formatGrowthDisplay(opp)}
                  </td>
                  <td className="px-3 py-3 align-middle md:px-4">
                    <span
                      className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${TIER_STYLE[tier]}`}
                    >
                      {TIER_LABEL[tier]}
                    </span>
                  </td>
                  <td className="px-3 py-3 pr-4 align-middle md:px-4">
                    <MovementCell movement={row.movement} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {entries.length === 0 && !loading ? (
        <p className="px-4 py-6 text-center text-sm text-zinc-500">
          {error || message || "No qualified opportunities in the latest snapshot yet."}
        </p>
      ) : null}
    </div>
  );
}
